/** @format */

import React, { FC, useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { Card, Select, Option } from '@king-design/react'
import { useAsyncFn } from 'react-use'
import { request } from 'src/utils'
import Api from 'src/api/ApiCloud'
import lodash from 'lodash'
import DataSource from './DataSource'
import Topology from './Topology'
import dayjs from 'dayjs'
import variable from 'src/static/styles/var'
interface IbTopologyProps {
  className?: string
  sourceList: any[]
}

export interface SearchValuesProps {
  dataSource: string | undefined
  fabric: string
  device: string[]
}

const initSearchValues: SearchValuesProps = {
  dataSource: undefined,
  fabric: '',
  device: []
}

export type TopoLayoutType = 'role' | 'disperse'

let timer = null
const IbTopology: FC<IbTopologyProps> = ({ className, sourceList }) => {
  // 布局
  const [layout, setLayout] = useState<TopoLayoutType>('role')
  // 设备筛选
  const [searchValues, setSearchValues] = useState<SearchValuesProps>(initSearchValues)
  // 获取数据时间
  const [lastSyncAt, setLastSyncAt] = useState<string>('')
  const [updateDiff, setUpdateDiff] = useState<number>(0)

  useEffect(() => {
    const localHeaderValues = JSON.parse(localStorage?.getItem('rock_search') || '{}')
    const dataSource = localHeaderValues?.dataSource || sourceList[0]?.ClusterID || ''
    fabric_fetch({
      ...searchValues,
      dataSource,
      device: localHeaderValues?.device || []
    })
  }, sourceList)

  // 获取Fabric
  const [{ value: fabricOption }, fabric_fetch] = useAsyncFn(async (values: SearchValuesProps) => {
    if (values?.dataSource) {
      const localHeaderValues = JSON.parse(localStorage?.getItem('rock_search') || '{}')
      const result = await request(Api.getNCEFabrics, {
        data: {
          DataSource: values?.dataSource
        }
      })
      const data = result?.data?.ListFabricsResult?.Data
      if (data.length) {
        setSearchValues({
          ...values,
          fabric: localHeaderValues?.fabric || data[0].FabricID || ''
        })
        return data
      }
    }
  }, [])

  // 修改数据源、设备
  const changeDeviecValues = (values: SearchValuesProps) => {
    setSearchValues(values)
    localStorage.setItem('rock_search', JSON.stringify(values))
  }

  // 初始化请求所有设备跟链路
  const updateGraph = () => {
    node_fetch()
    edge_fetch()
  }

  // 设备-跟Fabric相关联
  useEffect(() => {
    node_fetch()
  }, [searchValues.fabric])
  // 链路-跟数据源相关联
  useEffect(() => {
    edge_fetch()
  }, [searchValues.dataSource])

  // 获取所有设备
  const [{ value: allnode, loading: allnode_loading }, node_fetch] = useAsyncFn(async () => {
    if (searchValues.dataSource && searchValues.fabric) {
      const result = await request(Api.getNCENodes, {
        data: {
          DataSource: searchValues.dataSource,
          FabricID: searchValues.fabric
        }
      })
      return result?.data?.ListNetworkElementsResult?.Data || []
    } else {
      return []
    }
  }, [searchValues.dataSource, searchValues.fabric])

  // 获取所有链路
  const [{ value: alledge }, edge_fetch] = useAsyncFn(async () => {
    if (searchValues.dataSource) {
      const result = await request(Api.getNCEEdges, {
        data: { DataSource: searchValues.dataSource }
      })
      setLastSyncAt(result?.data?.ListLinksResult.LastSyncAt || '')
      const edges = []
      result?.data?.ListLinksResult?.Data?.forEach(item => {
        const { ANeDN, ZNeDN } = item
        const findIndex = edges.findIndex(edge => {
          return lodash.difference(edge.edge_id.split('==='), [ANeDN, ZNeDN])?.length === 0
        })
        if (findIndex >= 0) {
          edges[findIndex].links.push({ ...item })
        } else {
          edges.push({
            edge_id: `${ANeDN}===${ZNeDN}`,
            links: [{ ...item }]
          })
        }
      })
      return edges
    } else {
      return []
    }
  }, [searchValues.dataSource])

  // 筛选设备/链路
  const { nodelist, edgelist } = useMemo(() => {
    if (allnode?.length && alledge?.length) {
      allnode.forEach(node => {
        if (node.FabricNeRoles === 'Spine') {
          node.y_level = 1
        } else if (node.FabricNeRoles === 'ServerLeaf') {
          node.y_level = 2
        }
      })
      if (searchValues.device?.length) {
        const nodeids = []
        const edge_filter = []
        searchValues.device.forEach(nodeId => {
          edge_filter.push(
            ...alledge.filter(({ edge_id }) => {
              const [ANeDN, ZNeDN] = edge_id.split('===')
              const show = ANeDN === nodeId || ZNeDN === nodeId
              if (show) {
                nodeids.push(ANeDN, ZNeDN)
              }
              return show
            })
          )
        })
        const node_filter = allnode.filter(({ DN }) => nodeids.includes(DN))
        return {
          nodelist: node_filter,
          edgelist: edge_filter
        }
      }
      return {
        nodelist: allnode,
        edgelist: alledge
      }
    } else {
      return {
        nodelist: [],
        edgelist: []
      }
    }
  }, [searchValues.device, allnode, alledge])

  // 计算数据更新时间
  useEffect(() => {
    if (lastSyncAt) {
      const update = () => {
        const cur = dayjs()
        const diff = cur.diff(dayjs(lastSyncAt), 'seconds')
        setUpdateDiff(diff)
      }
      update()
      if (timer) {
        clearInterval(timer)
      }
      timer = setInterval(() => {
        update()
      }, 60 * 1000)
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [lastSyncAt])

  // 持续时间=>HH:mm
  const durationFromat = useMemo(() => {
    if (updateDiff && updateDiff > 5 * 60) {
      const hours = Math.floor(updateDiff / 3600)
      const minutes = Math.floor((updateDiff % 3600) / 60)
      return `${hours > 0 ? `${hours}小时` : ''}${minutes}分钟`
    } else {
      return ''
    }
  }, [updateDiff])

  return (
    <Card className={className}>
      <div className="layout">
        <div className="left_form">
          <div className="form_item">
            <span>布局</span>
            <Select
              width={150}
              value={layout}
              onChangeValue={value => {
                setLayout(value)
              }}
            >
              <Option value="role">角色布局</Option>
              <Option value="disperse">分散布局</Option>
            </Select>
          </div>
          {/* <div className="form_item form_item_switch">
            <CloudAlarm network="rock" />
          </div> */}

          {durationFromat ? (
            <p className="last_sync_at">
              当前为<span style={{ color: variable.primaryColor }}>{durationFromat}</span>
              前的数据，最新数据请<a onClick={updateGraph}>刷新</a>获取
            </p>
          ) : null}
        </div>
        {/* <HistoryDrawer network="rock" dataSource={searchValues.dataSource} /> */}
      </div>
      <DataSource
        sourceOptions={sourceList}
        fabricOptions={fabricOption || []}
        deviceOptions={allnode || []}
        allnode_loading={allnode_loading}
        formValues={searchValues}
        setFormValues={changeDeviecValues}
      />
      <Topology
        updateGraph={updateGraph}
        nodelist={nodelist}
        edgelist={edgelist}
        dataSource={searchValues.dataSource}
        searchNodes={searchValues.device}
        layout={layout}
      />
    </Card>
  )
}

export default styled(IbTopology)`
  .layout {
    display: flex;
    height: 48px;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0 16px;
    border-bottom: 1px solid #f0f2f4;
    position: relative;
    .left_form {
      display: flex;
      align-items: center;
      .form_item {
        margin-right: 16px;
        .form_item_label {
          padding-right: 10px;
        }
        & > span {
          padding-right: 10px;
        }
        &.link_analysis .k-btn {
          margin-left: 16px;
        }
        &.form_item_switch .k-btn {
          margin-left: 16px;
        }
      }
      .last_sync_at {
        margin: 0;
        line-height: 30px;
      }
    }
  }
`
